import React from 'react';
import { Link } from 'react-router-dom';
import { useLang, ACTIVE_LANGUAGES as LANGUAGES } from '@/context/LangContext';
import { TID } from '@/constants/testIds';
import { Brain, Layers, Users, Languages, Trophy, Zap, ChevronRight, Download, Target, Eye } from 'lucide-react';
import DirectInstallButton from '@/components/DirectInstallButton';

const LOGO = '/logo.png';
const HERO_BG = 'https://images.unsplash.com/photo-1504450758481-7338eba7524a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxuYmElMjBzdGFkaXVtfGVufDB8fHx8MTc4MTQ0NDAwMXww&ixlib=rb-4.1.0&q=85';
const GAME_IMG = 'https://images.pexels.com/photos/6076502/pexels-photo-6076502.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="bg-card-steel border border-gold/30 rounded-lg p-6 hover-lift">
      <div className="w-12 h-12 rounded-md border border-gold/50 flex items-center justify-center text-gold mb-4">
        <Icon className="w-6 h-6" strokeWidth={1.6} />
      </div>
      <h3 className="font-h uppercase tracking-wider text-xl text-white mb-2">{title}</h3>
      <p className="text-gray-300 leading-relaxed text-sm">{desc}</p>
    </div>
  );
}

export default function Landing() {
  const { t, lang, setLang } = useLang();
  // Pull live playbook counter so the landing reflects the real library size
  // — never lies, grows automatically as new plays are added to db.plays.
  const [playsCount, setPlaysCount] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playshub/counter`)
      .then((r) => r.json())
      .then((d) => { if (alive) setPlaysCount(d?.total || 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-navy text-white">
      {/* Top bar */}
      <header className="border-b border-gold/15 bg-navy-deep/70 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="PlayMaster IA" className="h-20 sm:h-28 lg:h-32" />
          </Link>
          <div className="flex items-center gap-2">
            <select
              value={lang}
              onChange={e => setLang(e.target.value)}
              data-testid={TID.langSwitcher}
              className="bg-navy-deep border border-gold/30 text-gold text-xs px-2 py-1.5 rounded font-h uppercase tracking-wider"
            >
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag}</option>)}
            </select>
            <Link
              to="/install"
              data-testid="header-install-btn"
              className="inline-flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition font-h uppercase tracking-wider text-xs"
              title={t('landing_install_app')}
              aria-label={t('landing_install_app')}
            >
              <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('landing_install_short')}</span>
            </Link>
            <Link
              to="/login"
              data-testid={TID.ctaLogin}
              className="inline-flex items-center gap-1 px-3 sm:px-4 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition font-h uppercase tracking-wider text-xs sm:text-sm"
            >
              {t('login')}
            </Link>
            <Link
              to="/register"
              data-testid={TID.ctaGetStarted}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-gold text-navy hover:bg-gold-light transition font-h uppercase tracking-wider text-sm font-semibold"
            >
              {t('register')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section data-testid={TID.landingHero} className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/85 to-navy" />
        <div className="absolute inset-0 grain opacity-30" />

        <div className="relative max-w-7xl mx-auto px-5 py-20 lg:py-32 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/40 rounded-full px-3 py-1 mb-6">
              <Zap className="w-3 h-3 text-gold" />
              <span className="text-xs font-mono uppercase tracking-[0.25em] text-gold">by PlayMaster IA</span>
            </div>

            <h1 className="font-h uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] mb-4">
              <span className="block text-white">{t('landing_hero_h1_a')}</span>
              <span className="block gold-text-gradient">{t('landing_hero_h1_b')}</span>
            </h1>

            <h2 className="text-xl sm:text-2xl text-gold/90 font-h tracking-tight mb-6 leading-snug">
              {t('landing_hero_h2')}
            </h2>

            <p className="text-base text-gray-300 max-w-2xl mb-4 leading-relaxed">
              {t('landing_hero_para')}
            </p>

            <p className="text-base text-gray-400 max-w-2xl mb-8 leading-relaxed">
              {t('landing_hero_para_2')}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light transition shadow-lg shadow-gold/20"
              >
                {t('register')} <ChevronRight className="w-5 h-5" />
              </Link>
              {/* Direct Install — replaces the old /install link with the
                  native beforeinstallprompt flow so it's truly one tap. */}
              <DirectInstallButton variant="primary" />
              <Link
                to="/blog"
                data-testid="landing-blog-btn"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-md border-2 border-gold/30 text-gray-300 font-h uppercase tracking-wider font-semibold hover:border-gold/60 hover:text-gold transition"
              >
                Blog
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-md border-2 border-gold/30 text-gray-300 font-h uppercase tracking-wider font-semibold hover:border-gold/60 hover:text-gold transition"
              >
                {t('login')}
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[
                { n: playsCount != null ? `${playsCount}+` : '120+', l: t('landing_stat_plays') },
                { n: '3', l: t('landing_stat_languages') },
                { n: 'AI', l: t('landing_stat_coach_24') },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-h text-3xl text-gold">{s.n}</div>
                  <div className="text-xs uppercase tracking-widest text-gray-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-gold/40 to-transparent blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border-2 border-gold/40 shadow-2xl">
                <img src={GAME_IMG} alt="basketball action" className="w-full h-auto" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">{t('landing_live_preview')}</div>
                  <div className="font-h text-2xl uppercase text-white">Coach Rivera · Horns Flare</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="mb-12">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-3">{t('landing_cap_eyebrow')}</div>
          <h2 className="font-h uppercase text-4xl sm:text-5xl font-bold tracking-tight">
            {t('landing_cap_h1_a')}<br />{t('landing_cap_h1_b')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Feature icon={Layers} title={t('landing_f_playbook_t')} desc={t('landing_f_playbook_d')} />
          <Feature icon={Brain} title={t('landing_f_ai_t')} desc={t('landing_f_ai_d')} />
          <Feature icon={Users} title={t('landing_f_roster_t')} desc={t('landing_f_roster_d')} />
          <Feature icon={Languages} title={t('landing_f_langs_t')} desc={t('landing_f_langs_d')} />
          <Feature icon={Trophy} title={t('landing_f_live_t')} desc={t('landing_f_live_d')} />
          <Feature icon={Zap} title={t('landing_f_editor_t')} desc={t('landing_f_editor_d')} />
          {/* New 2026 modules — surfaced here so visitors know the platform is
              already an analytics + AI training stack, not just a playbook. */}
          <Feature icon={Eye} title={t('landing_f_iqsim_t')} desc={t('landing_f_iqsim_d')} />
          <Feature icon={Trophy} title={t('landing_f_iqscore_t')} desc={t('landing_f_iqscore_d')} />
          <Feature icon={Target} title={t('landing_f_shot_t')} desc={t('landing_f_shot_d')} />
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-gold/15 bg-navy-deep">
        <div className="max-w-5xl mx-auto px-5 py-16 text-center">
          <h2 className="font-h uppercase text-4xl sm:text-5xl mb-4">{t('landing_cta_h1')}</h2>
          <p className="text-gray-300 mb-8">{t('landing_cta_demo')} <span className="font-mono text-gold">coach@playmasteria.com</span> / <span className="font-mono text-gold">Coach#2026</span></p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light transition"
          >
            {t('landing_cta_btn')} <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-gold/15 px-5 py-6 text-center text-xs text-gray-500 space-y-2">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link to="/privacy" className="text-gold hover:underline" data-testid="footer-privacy-link">
            {t('privacy_policy')}
          </Link>
          <span className="text-gray-700">·</span>
          <Link to="/terms" className="text-gold hover:underline" data-testid="footer-terms-link">
            {t('terms_of_service')}
          </Link>
          <span className="text-gray-700">·</span>
          <a href="mailto:playmasteria@gmail.com" className="text-gold hover:underline">{t('contact')}</a>
        </div>
        <div>© 2026 PlayMaster IA · The Future of Basketball Playbooks</div>
      </footer>
    </div>
  );
}
